import * as React from 'react'
import Head from 'next/head'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Alert from '@material-ui/lab/Alert'
import Snackbar from '@material-ui/core/Snackbar'
import { Formit, Form, Field } from '@oakwood/formit'
import { useGlobal, useUser } from 'api'
import StoryblokService from 'utils/storyblok-service'
import Container from 'components/Container'
import FormitButton from 'containers/FormitButton'
import Section from 'components/Section'
import TextField from 'components/TextField'
import Typography from 'components/Typography'
import { useTranslations } from 'containers/Translations/TranslationsContext'
import { Page } from './[[...uri]]'

export const useStyles = makeStyles(() => ({
  formSpacing: {
    marginTop: 24,
  },
}))

const ambassadorApplicationPendingPage = 'ambassador-program-waiting-for-confirmation'

function AmbassadorApplicationPage(props) {
  const { user, pendingAmbassadorApplication, onAmbassadorApplication, socialMediaAccounts } =
    useUser()

  const { market } = useGlobal()

  const classes = useStyles(props)

  const router = useRouter()

  const [error, setError] = React.useState()

  const handleSubmit = React.useCallback(
    async (values) => {
      try {
        await onAmbassadorApplication({ affiliate_application: values })

        router.push(`/${ambassadorApplicationPendingPage}`)
      } catch (err) {
        setError(err)
      }
    },
    [onAmbassadorApplication, router],
  )

  const handleSnackbarClose = React.useCallback(() => {
    setError(false)
  }, [])
  const t = useTranslations()

  return (
    <>
      <Head>
        <title>{t('Web.AmbassadorApplication.Title')}</title>
      </Head>

      <Section gutters="padding">
        {pendingAmbassadorApplication ? (
          <Page {...props} />
        ) : (
          <>
            <Container>
              <Typography align="center" variant="h4" gutterBottom>
                {t('Web.AmbassadorApplication.AboutYourself')}
              </Typography>
            </Container>

            <Container maxWidth="xs">
              <Formit
                validationErrors={{}}
                initialValues={{
                  firstName: user?.firstName || '',
                  lastName: user?.lastName || '',
                  email: user?.email || '',
                  audienceSize: '',
                  facebook: socialMediaAccounts.facebook || '',
                  twitter: socialMediaAccounts.twitter || '',
                  instagram: socialMediaAccounts.instagram || '',
                  tiktok: socialMediaAccounts.tiktok || '',
                  sponsorAmbassadorCode: '',
                  personalId: '',
                  city: user?.addresses ? user?.addresses[0].city || '' : '',
                }}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                <Form className={classes.formSpacing}>
                  <Field
                    component={TextField}
                    name="firstName"
                    label={t('Web.AmbassadorApplication.FirstName')}
                    variant="filled"
                    margin="normal"
                    fullWidth
                    required
                    autoFocus
                    id="kalla-abassador-application-name" // Makes `label` and `helperText` accessible for screen readers.
                  />

                  <Field
                    component={TextField}
                    name="lastName"
                    label={t('Web.AmbassadorApplication.LastName')}
                    variant="filled"
                    margin="normal"
                    fullWidth
                    required
                    id="kalla-abassador-application-name" // Makes `label` and `helperText` accessible for screen readers.
                  />

                  <Field
                    component={TextField}
                    name="email"
                    type="email"
                    label={t('Web.AmbassadorApplication.Email')}
                    variant="filled"
                    margin="normal"
                    fullWidth
                    required
                    id="kalla-abassador-application-email" // Makes `label` and `helperText` accessible for screen readers.
                  />

                  {market.mlmMarket && (
                    <>
                      <Field
                        component={TextField}
                        name="sponsorAmbassadorCode"
                        label={t('Web.AmbassadorApplication.AmbassadorCode')}
                        variant="filled"
                        margin="normal"
                        fullWidth
                        id="kalla-abassador-application-sponsor-ambassador-code" // Makes `label` and `helperText` accessible for screen readers.
                      />

                      <Field
                        component={TextField}
                        name="city"
                        label={t('Web.AmbassadorApplication.City')}
                        variant="filled"
                        margin="normal"
                        fullWidth
                        required
                        id="kalla-abassador-application-city" // Makes `label` and `helperText` accessible for screen readers.
                      />
                    </>
                  )}
                  <Field
                    component={TextField}
                    name="personalId"
                    label={t('Web.AmbassadorApplication.PersonalId')}
                    variant="filled"
                    margin="normal"
                    fullWidth
                    required
                    id="kalla-abassador-application-personal-id" // Makes `label` and `helperText` accessible for screen readers.
                  />

                  <div className={classes.formSpacing}>
                    <Typography>{t('Web.AmbassadorApplication.Acknowledgement')}</Typography>

                    {!market.mlmMarket && (
                      <>
                        <Field
                          component={TextField}
                          name="audienceSize"
                          label={t('Web.AmbassadorApplication.AudienceSize')}
                          type="number"
                          variant="filled"
                          margin="normal"
                          fullWidth
                          id="kalla-abassador-application-audienceSize" // Makes `label` and `helperText` accessible for screen readers.
                        />

                        <Typography>
                          {t('Web.AmbassadorApplication.AcknowledgementProfiles')}
                        </Typography>
                      </>
                    )}
                    <Field
                      component={TextField}
                      name="facebook"
                      label={t('Web.AmbassadorApplication.Facebook')}
                      variant="filled"
                      margin="normal"
                      fullWidth
                      id="kalla-abassador-application-facebook" // Makes `label` and `helperText` accessible for screen readers.
                    />

                    <Field
                      component={TextField}
                      name="twitter"
                      label={t('Web.AmbassadorApplication.Twitter')}
                      variant="filled"
                      margin="normal"
                      fullWidth
                      id="kalla-abassador-application-twitter" // Makes `label` and `helperText` accessible for screen readers.
                    />

                    <Field
                      component={TextField}
                      name="instagram"
                      label={t('Web.AmbassadorApplication.Instagram')}
                      variant="filled"
                      margin="normal"
                      fullWidth
                      id="kalla-abassador-application-instagram" // Makes `label` and `helperText` accessible for screen readers.
                    />

                    <Field
                      component={TextField}
                      name="tiktok"
                      label={t('Web.AmbassadorApplication.TikTok')}
                      variant="filled"
                      margin="normal"
                      fullWidth
                      id="kalla-abassador-application-tiktok" // Makes `label` and `helperText` accessible for screen readers.
                    />
                  </div>

                  {error && (
                    <Snackbar
                      open={error}
                      autoHideDuration={6000}
                      onClose={handleSnackbarClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                      <Alert variant="filled" severity="error">
                        {Object.values(error?.data?.errors).map((k, idx) => (
                          <div key={idx}>{k}</div>
                        ))}
                      </Alert>
                    </Snackbar>
                  )}

                  <div className={classes.formSpacing}>
                    <FormitButton size="large" variant="contained" type="submit" fullWidth>
                      {t('Web.AmbassadorApplication.Apply')}
                    </FormitButton>
                  </div>
                </Form>
              </Formit>
            </Container>
          </>
        )}
      </Section>
    </>
  )
}

export async function getServerSideProps() {
  const { publicRuntimeConfig } = getConfig()

  const page = await StoryblokService.get(`cdn/stories/${ambassadorApplicationPendingPage}`, {
    resolve_relations: 'author',
    version: publicRuntimeConfig.STORYBLOK_ENV,
  })

  return {
    props: {
      page,
    },
  }
}

export default AmbassadorApplicationPage
